import React from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import CookieDisclaimer from "../../components/CookieDisclaimer";
import SEO from "../../components/SEO";
import "../../styles.css";

export default function AchievementTracker() {
  return (
    <>
      <SEO
        title="Achievement Tracker - PS5 Achievement Counter for Streamers"
        description="Transform every achievement pop-up into a live on-stream achievement counter! Learn how to install and customize the Achievement Tracker for your stream."
        keywords="Achievement Tracker, PS5 achievements, stream overlay, achievement counter, Streamer.bot, OBS overlay, PlayStation trophies, Steam achievements, Xbox achievements, achievement hunter"
        url="https://zemaxi.com/tools/achievement-tracker"
      />
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="trophy-tracker-hero">
          <div className="container">
            <h1>Achievement <span className="highlight">Tracker</span></h1>
            <p>Transform every achievement pop-up into a live on-stream achievement counter!</p>
          </div>
        </section>

        <section className="trophy-tracker-content">
          <div className="container">
            <div className="trophy-tracker-image">
              <img src="https://zemaxi.com/images/products/AchievementTracker.png" alt="Achievement Tracker Preview" />
            </div>

            <div className="trophy-tracker-description">
              <h2>What is <span className="highlight">Achievement Tracker?</span></h2>
              <p>The Achievement Tracker automatically detects and tracks your achievements for Steam, Xbox and PlayStation in real-time! Every time you earn an achievement/trophy, your stream overlay updates instantly, showing your viewers your progress throughout your gaming session. Perfect for achievement hunters and streamers who want to showcase their accomplishments.</p>
            </div>


            <div className="trophy-tracker-install">
              <h2>Platform <span className="highlight">Tutorials</span></h2>
              <p>Watch the platform-specific setup guides below. Written setup steps have been removed and this page now uses video tutorials instead.</p>

              <div className="achievement-platform-videos">
                <div className="achievement-platform-video-card">
                  <h3>Steam <span className="highlight">Tutorial</span></h3>
                  <div className="video-embed-container achievement-platform-embed">
                    <iframe
                      width="560"
                      height="315"
                      src="https://www.youtube.com/embed/dQw4w9WgXcQ?rel=0"
                      title="Achievement Tracker Steam Tutorial"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                <div className="achievement-platform-video-card">
                  <h3>Xbox <span className="highlight">Tutorial</span></h3>
                  <div className="video-embed-container achievement-platform-embed">
                    <iframe
                      width="560"
                      height="315"
                      src="https://www.youtube.com/embed/dQw4w9WgXcQ?rel=0"
                      title="Achievement Tracker Xbox Tutorial"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                <div className="achievement-platform-video-card">
                  <h3>PlayStation <span className="highlight">Tutorial</span></h3>
                  <div className="video-embed-container achievement-platform-embed">
                    <iframe
                      width="560"
                      height="315"
                      src="https://www.youtube.com/embed/dQw4w9WgXcQ?rel=0"
                      title="Achievement Tracker PlayStation Tutorial"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>

            <div className="trophy-tracker-cta">
              <h2>Ready to <span className="highlight">Get Started?</span></h2>
              <div className="beta-section">
                <a href="https://beta.zemaxi.com/achievement-tracker" target="_blank" rel="noopener noreferrer" className="btn btn-beta">
                  <i className="fas fa-flask"></i> Use the Achievement Tracker (Beta)
                </a>
              </div>
              <div className="cta-buttons">
                <a href="/files/trophy-tracker.zip" download className="btn btn-primary">
                  <i className="fas fa-download"></i> Download the Achievement Tracker application
                </a>
              </div>
            </div>

            <div className="trophy-tracker-faq">
              <h2>Frequently Asked <span className="highlight">Questions</span></h2>
              <div className="faq-container">
                <div className="faq-item">
                  <h3>What is Achievement Tracker?</h3>
                  <p>Achievement Tracker is a free OBS overlay that automatically detects Steam, Xbox and PlayStation achievement pop-ups on screen and displays a live achievement count on your stream.</p>
                </div>

                <div className="faq-item">
                  <h3>What do I need to use Achievement Tracker?</h3>
                  <p>You need OBS Studio, Streamer.bot, and optionally the Achievement Tracker application (a small Windows app) along with the Lua script. The overlay is added to OBS as a Browser Source.</p>
                </div>

                <div className="faq-item">
                  <h3>How does the achievement detection actually work?</h3>
                  <p>The detector watches a region of your screen (or a capture card feed) and uses OCR (text recognition) to look for the "Trophy earned!" (for PlayStation) and "Achievement Unlocked!" (For Xbox) text that appears when you unlock an achievement/trophy. It requires two consecutive detections to avoid false positives, and has a cooldown to prevent double-counting.</p>
                </div>

                <div className="faq-item">
                  <h3>Do I need a capture card?</h3>
                  <p>No. By default the detector captures directly from your monitor. If you play on a TV that isn't your PC monitor, you can use a capture card and point the detector at that OBS source instead. Both modes are supported.</p>
                </div>

                <div className="faq-item">
                  <h3>Do I need to use the Achievement Tracker application?</h3>
                  <p>No. The Achievement Tracker application is optional. You can use the overlay without it, but it provides the added bonus of automatically detecting an achievement/trophy in real-time vs a poll interval.</p>
                </div>

                <div className="faq-item">
                  <h3>How do I customise the look of the overlay?</h3>
                  <p>Use the Trophy Tracker Customizer page. You can change the style preset (PlayStation theme, generic, etc.), font, colours, icon type, font size, alignment, and more. It generates a URL with your settings baked in — just paste that URL into an OBS Browser Source.</p>
                </div>

                <div className="faq-item">
                  <h3>My count seems wrong — how does it stay accurate?</h3>
                  <p>On startup the overlay immediately queries your selected platform for your real earned count. After each detection it shows an optimistic +1 instantly, then verifies with your platform a few seconds later and corrects if needed. The count can only go up during verification (it uses the higher of your platform vs. local), so you'll never see it randomly drop.</p>
                </div>

                <div className="faq-item">
                  <h3>Can I track trophies for a specific game?</h3>
                  <p>Yes, if your selected Platform is connected. In the customizer, search for a game by name, select it, and the overlay will show your earned count out of the game's total achievements/trophies. You can also enable a progress bar and display the game name on the overlay.</p>
                </div>

                <div className="faq-item">
                  <h3>I switched games mid-stream, do I need to do anything?</h3>
                  <p>If you're tracking a specific game, you'll want to switch the selected game in the customizer so the total and progress is updated.</p>
                </div>

                <div className="faq-item">
                  <h3>The detector isn't picking up my achievements/trophies, what should I check?</h3>
                  <ul>
                    <li>Make sure the detection region covers where achievement/trophy pop-ups appear (usually top-right for PS5, top-left for PS4 and bottom-center for Xbox). You can use the "Select Detection Region" button in OBS to set a custom region.</li>
                    <li>Check that the correct capture source is selected (screen capture vs. a specific OBS source/capture card).</li>
                    <li>Ensure OCR dependencies are installed — the detector will warn you on startup if OCR is unavailable and fall back to less reliable template matching.</li>
                    <li>Try enabling "Save Debug Images" in the settings and check the saved screenshots to see what the detector is actually seeing.</li>
                    <li>Make sure the cooldown isn't set too high (default is 5 seconds).</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
